import React, { useState } from 'react';
import './PokemonCardGrid.css';

// Rarity abbreviation mapping - keys are lowercase for matching
const RARITY_ABBREVIATIONS = {
  'common': 'C',
  'uncommon': 'U',
  'rare': 'R',
  'rare holo': 'R',
  'rare holo v': 'V',
  'rare holo vmax': 'VMAX',
  'rare holo vstar': 'VSTAR',
  'rare holo gx': 'GX',
  'rare holo ex': 'EX',
  'double rare': 'RR',
  'ultra rare': 'UR',
  'secret rare': 'SR',
  'rare secret': 'SR',
  'rare shiny': 'SH',
  'rare shiny gx': 'SHGX',
  'rare ace': 'ACE',
  'rare break': 'BRK',
  'rare prime': 'PRM',
  'rare prism star': 'PS',
  'amazing rare': 'AR',
  'radiant rare': 'RAD',
  'illustration rare': 'IR',
  'special illustration rare': 'SIR',
  'hyper rare': 'HR',
  'promo': 'P',
  'classic collection': 'CC',
  'trainer gallery rare holo': 'TG'
};

function getRarityAbbreviation(rarity) {
  if (!rarity) return '?';
  const normalized = rarity.toLowerCase().trim();
  
  // Direct match
  if (RARITY_ABBREVIATIONS[normalized]) {
    return RARITY_ABBREVIATIONS[normalized];
  }
  
  // Check if any key is contained in the rarity
  for (const [key, abbrev] of Object.entries(RARITY_ABBREVIATIONS)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return abbrev;
    }
  }
  
  // Fallback: use first letters of each word
  const words = rarity.split(/\s+/);
  if (words.length > 1) {
    return words.map(w => w.charAt(0).toUpperCase()).join('');
  }
  
  return rarity.charAt(0).toUpperCase();
}

function getRarityClass(rarity) {
  if (!rarity) return 'common';
  const normalized = rarity.toLowerCase();
  if (normalized.includes('secret') || normalized.includes('hyper') || normalized.includes('special illustration')) return 'mythic';
  if (normalized.includes('ultra') || normalized.includes('illustration rare') || normalized.includes('double rare')) return 'rare';
  if (normalized.includes('rare')) return 'rare';
  if (normalized.includes('uncommon')) return 'uncommon';
  return 'common';
}

function PokemonCardGrid({ cards, onCardClick, apiUrl }) {
  return (
    <div className="pokemon-card-grid">
      {cards.map((card) => (
        <PokemonCardItem 
          key={card.id} 
          card={card} 
          onClick={() => onCardClick(card)}
          apiUrl={apiUrl}
        />
      ))}
    </div>
  );
}

function PokemonCardItem({ card, onClick, apiUrl }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Image path: /pokemon/<lang>/<id>.png
  const lang = card.lang || 'en';
  const imageUrl = `${apiUrl}/images/pokemon/${lang}/${card.id}.png`;

  const rarityClass = getRarityClass(card.rarity);

  const getColorClass = (types) => {
    if (!types || types.length === 0) return 'color-c';
    if (types.length > 1) return 'color-multi';
    const typeMap = {
      'fire': 'r',
      'water': 'u',
      'grass': 'g',
      'lightning': 'w',
      'psychic': 'b',
      'fighting': 'r',
      'darkness': 'b',
      'metal': 'c',
      'fairy': 'w',
      'dragon': 'multi',
      'colorless': 'c'
    };
    return `color-${typeMap[types[0].toLowerCase()] || 'c'}`;
  };

  return (
    <div 
      className={`pokemon-card-item rarity-${rarityClass}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className="pokemon-card-image-container">
        {!imageLoaded && !imageError && (
          <div className="pokemon-card-placeholder">
            <div className="pokemon-placeholder-icon">⚔</div>
            <div className="pokemon-placeholder-text">Loading...</div>
          </div>
        )}
        {!imageError ? (
          <img
            src={imageUrl}
            alt={card.name}
            className={`pokemon-card-image ${imageLoaded ? 'loaded' : ''}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            loading="lazy"
          />
        ) : (
          <div className="pokemon-card-placeholder error">
            <div className={`pokemon-placeholder-colors ${getColorClass(card.types)}`}>
              {card.types?.[0]?.charAt(0) || card.supertype?.charAt(0) || '?'}
            </div>
            <div className="pokemon-placeholder-name">{card.name}</div>
            <div className="pokemon-placeholder-type">{card.supertype}</div>
          </div>
        )}
        <div className="pokemon-card-overlay">
          <span className="pokemon-view-label">View Details</span>
        </div>
      </div>
      <div className="pokemon-card-info">
        <h3 className="pokemon-card-name" title={card.name}>{card.name}</h3>
        <div className="pokemon-card-meta">
          <span className={`pokemon-card-rarity rarity-${rarityClass}`}>
            {getRarityAbbreviation(card.rarity)}
          </span>
          <span className="pokemon-card-set" title={card.set}>{card.setCode}</span>
          {card.hp && (
            <span className="pokemon-card-hp">{card.hp} HP</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default PokemonCardGrid;
